import {
  defaultBaseQuery,
  transformRequest,
  transformErrorResponse,
  transformSuccessResponse,
} from "@/services";
import { createApi } from "@reduxjs/toolkit/query/react";

const administrationAPI = createApi({
  reducerPath: "administrationAPI",
  baseQuery: defaultBaseQuery,
  tagTypes: ["AttendanceHistory", "DepartmentAttendanceReport", "AttendanceReport", "ShiftMaster", "ShiftScheduling", "ShiftAssignmentPattern", "CommonDropdowns", "LeaveStages"],
  endpoints: (builder) => ({
    getAttendanceHistory: builder.query({
      query: ({ currentDate, page = 0, size = 10, searchText = '', sortBy = '', sortDir = '', departmentId = '', designationId = '' } = {}) => ({
        url: `/attendance`,
        method: "GET",
        params: { currentDate, page, size, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }), ...(departmentId && { encryptedDepartmentId: departmentId }), ...(designationId && { encryptedDesignationId: designationId }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["AttendanceHistory"],
    }),
    exportAttendanceRegisterExcel: builder.query({
      query: ({ currentDate, sortBy = '', sortDir = '', departmentId = '', designationId = '' } = {}) => ({
        url: `/attendance/register/excel`,
        method: "GET",
        params: { currentDate, ...(sortBy && { sortBy }), ...(sortDir && { sortDir }), ...(departmentId && { encryptedDepartmentId: departmentId }), ...(designationId && { encryptedDesignationId: designationId }) },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `attendance_register_${currentDate}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
        cache: 'no-cache',
      }),
      transformErrorResponse: transformErrorResponse,
    }),
    markAttendance: builder.mutation({
      query: (data) => ({
        url: `/attendance`,
        method: "POST",
        body: transformRequest(data),
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["AttendanceHistory"],
    }),
    getDepartmentAttendanceReport: builder.query({
      query: ({ date, page = 0, size = 10, searchText = '', sortBy = '', sortDir = '' } = {}) => ({
        url: `/attendance-report/department`,
        method: "GET",
        params: { date, page, size, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["DepartmentAttendanceReport"],
    }),
    getAttendanceReport: builder.query({
      query: ({ fromDate, toDate, page = 0, size = 10, searchText = '', sortBy = '', sortDir = '', departmentId = '', designationId = '' } = {}) => ({
        url: `/attendance-report`,
        method: "GET",
        params: { fromDate, toDate, page, size, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }), ...(departmentId && { departmentId }), ...(designationId && { designationId }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["AttendanceReport"],
    }),
    exportAttendanceReportExcel: builder.query({
      query: ({ fromDate, toDate, page = 0, size = 10, searchText = '', sortBy = '', sortDir = '', departmentId = '', designationId = '' } = {}) => ({
        url: `/attendance-report/report/excel`,
        method: "GET",
        params: { fromDate, toDate, page, size, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }), ...(departmentId && { departmentId }), ...(designationId && { designationId }) },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `attendance_report_${fromDate}_${toDate}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
        cache: 'no-cache',
      }),
      transformErrorResponse: transformErrorResponse,
    }),
    getShiftMasterList: builder.query({
      query: ({ page = 0, size = 10, searchText = '', sortDir = '' } = {}) => ({
        url: `/shift-master`,
        method: "GET",
        params: { page, size, ...(searchText && { searchText }), ...(sortDir && { sortDir }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["ShiftMaster"],
    }),
    getShiftMasterById: builder.query({
      query: (id) => ({
        url: `/shift-master/${id}`,
        method: "GET",
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["ShiftMaster"],
    }),
    getCommonDropdowns: builder.query({
      query: () => ({
        url: `/dropdowns`,
        method: "GET",
        params: { pageName: "COMMON_DROPDOWNS" },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["CommonDropdowns"],
    }),
    saveShiftMaster: builder.mutation({
      query: (data) => ({
        url: `/shift-master`,
        method: "POST",
        body: transformRequest(data),
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["ShiftMaster"],
    }),
    deleteShiftMaster: builder.mutation({
      query: (id) => ({
        url: `/shift-master/${id}`,
        method: "DELETE",
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["ShiftMaster"],
    }),
    // GET /shift/by-person?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
    getShiftSchedulingByPerson: builder.query({
      query: ({ fromDate, toDate, employeeCode } = {}) => {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (employeeCode) params.employeeCode = employeeCode;
        return {
          url: `/shift/by-person`,
          method: "GET",
          params,
        };
      },
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["ShiftScheduling"],
    }),
    // GET /shift?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
    getShiftScheduling: builder.query({
      query: ({ fromDate, toDate, searchText, encryptedDepartmentId } = {}) => {
        const params = {};
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (searchText) params.searchText = searchText;
        if (encryptedDepartmentId) params.encryptedDepartmentId = encryptedDepartmentId;
        return {
          url: `/shift`,
          method: "GET",
          params,
        };
      },
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["ShiftScheduling"],
    }),
    // PUT /shift/update-shift - Update shift assignment
    updateShiftAssignment: builder.mutation({
      query: (data) => ({
        url: `/shift/update-shift`,
        method: "PUT",
        body: transformRequest(data),
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["ShiftScheduling"],
    }),
    // POST /pattern/person - Save shift assignment pattern
    saveShiftAssignmentPattern: builder.mutation({
      query: (data) => ({
        url: `/pattern/single-shift/person`,
        method: "POST",
        body: transformRequest(data),
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["ShiftAssignmentPattern", "ShiftScheduling"],
    }),
    getLeaveStages: builder.query({
      query: () => ({
        url: `/leave-stages`,
        method: "GET",
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ["LeaveStages"],
    }),
    getPersonList: builder.query({
      query: () => ({
        url: `/person`,
        method: "GET",
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getPersonListWithFilters: builder.query({
      query: ({ departmentId, designationId } = {}) => {
        const params = {};
        if (departmentId) params.departmentId = departmentId;
        if (designationId) params.designationId = designationId;
        return {
          url: `/person`,
          method: "GET",
          params,
        };
      },
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    saveLeaveStages: builder.mutation({
      query: (data) => ({
        url: `/leave-stages`,
        method: "POST",
        body: transformRequest(data),
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ["LeaveStages"],
    }),
    // GET /attendance/punch-details?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD&personId=...
    getPunchDetails: builder.query({
      query: ({ fromDate, toDate, personId } = {}) => ({
        url: `/attendance/punch-details`,
        method: "GET",
        params: { fromDate, toDate, personId },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
  }),
});

export const {
  useLazyGetAttendanceHistoryQuery,
  useLazyExportAttendanceRegisterExcelQuery,
  useMarkAttendanceMutation,
  useLazyGetDepartmentAttendanceReportQuery,
  useLazyGetAttendanceReportQuery,
  useLazyExportAttendanceReportExcelQuery,
  useGetShiftMasterListQuery,
  useGetShiftMasterByIdQuery,
  useGetCommonDropdownsQuery,
  useSaveShiftMasterMutation,
  useDeleteShiftMasterMutation,
  useLazyGetShiftSchedulingByPersonQuery,
  useGetShiftSchedulingQuery,
  useLazyGetShiftSchedulingQuery,
  useUpdateShiftAssignmentMutation,
  useSaveShiftAssignmentPatternMutation,
  useGetLeaveStagesQuery,
  useSaveLeaveStagesMutation,
  useGetPersonListQuery,
  useLazyGetPersonListQuery,
  useGetPersonListWithFiltersQuery,
  useLazyGetPersonListWithFiltersQuery,
  useGetPunchDetailsQuery,
} = administrationAPI;

export default administrationAPI;
